import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import type {
  Team,
  TeamInvitation,
  TeamMember,
  TeamMemberPosition,
  UserWithToken,
} from '@ygoargentina/sdk/api/api.types';

const TEAM_FIELDS = gql`
  fragment TeamFields on Team {
    id
    name
    slug
    logoUrl
    description
    isPublic
    membersCount
    owner {
      id
      name
      lastName
      picture
    }
    members {
      id
      teamId
      position
      joinedAt
      user {
        id
        name
        lastName
        picture
      }
    }
    links {
      id
      type
      url
      label
    }
    gameTypes {
      id
      name
    }
    formats {
      id
      name
    }
    createdAt
    updatedAt
  }
`;

const TEAM_INVITATION_FIELDS = gql`
  fragment TeamInvitationFields on TeamInvitation {
    id
    team {
      id
      name
      slug
      logoUrl
    }
    invitedUser {
      id
      name
      lastName
      picture
    }
    invitedBy {
      id
      name
      lastName
      picture
    }
    proposedPosition
    status
    message
    createdAt
    respondedAt
  }
`;

export interface TeamsFilters {
  search?: string;
  gameTypeId?: number;
  formatId?: number;
  ownerId?: number;
  memberUserId?: number;
}

export const getTeams = async (
  user: UserWithToken | null,
  filters?: TeamsFilters,
  limit?: number,
  offset?: number
): Promise<Team[]> => {
  const { teams } = await getGraphQLQueryClient(user).request<{ teams: Team[] }>(
    gql`
      ${TEAM_FIELDS}
      query GetTeams($filters: TeamFilters, $limit: Int, $offset: Int) {
        teams(filters: $filters, limit: $limit, offset: $offset) {
          ...TeamFields
        }
      }
    `,
    { filters, limit, offset }
  );
  return teams;
};

export const getTeam = async (
  user: UserWithToken | null,
  id: number
): Promise<Team> => {
  const { team } = await getGraphQLQueryClient(user).request<{ team: Team }>(
    gql`
      ${TEAM_FIELDS}
      query GetTeam($id: Int!) {
        team(id: $id) {
          ...TeamFields
        }
      }
    `,
    { id }
  );
  return team;
};

export const getTeamBySlug = async (
  user: UserWithToken | null,
  slug: string
): Promise<Team> => {
  const { team } = await getGraphQLQueryClient(user).request<{ team: Team }>(
    gql`
      ${TEAM_FIELDS}
      query GetTeamBySlug($slug: String!) {
        team(slug: $slug) {
          ...TeamFields
        }
      }
    `,
    { slug }
  );
  return team;
};

export const getMyTeams = async (user: UserWithToken): Promise<Team[]> => {
  const { myTeams } = await getGraphQLQueryClient(user).request<{
    myTeams: Team[];
  }>(
    gql`
      ${TEAM_FIELDS}
      query GetMyTeams {
        myTeams {
          ...TeamFields
        }
      }
    `
  );
  return myTeams;
};

export const getMyTeamInvitations = async (
  user: UserWithToken
): Promise<TeamInvitation[]> => {
  const { myTeamInvitations } = await getGraphQLQueryClient(user).request<{
    myTeamInvitations: TeamInvitation[];
  }>(
    gql`
      ${TEAM_INVITATION_FIELDS}
      query GetMyTeamInvitations {
        myTeamInvitations {
          ...TeamInvitationFields
        }
      }
    `
  );
  return myTeamInvitations;
};

export interface CreateTeamInput {
  name: string;
  description?: string;
  logoUrl?: string;
  ownerPosition: TeamMemberPosition;
  gameTypeIds?: number[];
  formatIds?: number[];
  links?: Array<{ type: string; url: string; label?: string }>;
}

export const createTeam = async (
  user: UserWithToken,
  input: CreateTeamInput
): Promise<Team> => {
  const { createTeam: team } = await getGraphQLQueryClient(user).request<{
    createTeam: Team;
  }>(
    gql`
      ${TEAM_FIELDS}
      mutation CreateTeam($input: CreateTeamInput!) {
        createTeam(input: $input) {
          ...TeamFields
        }
      }
    `,
    { input }
  );
  return team;
};

export interface UpdateTeamInput {
  name?: string;
  description?: string | null;
  logoUrl?: string | null;
  gameTypeIds?: number[];
  formatIds?: number[];
  links?: Array<{ type: string; url: string; label?: string }>;
}

export const updateTeam = async (
  user: UserWithToken,
  id: number,
  input: UpdateTeamInput
): Promise<Team> => {
  const { updateTeam: team } = await getGraphQLQueryClient(user).request<{
    updateTeam: Team;
  }>(
    gql`
      ${TEAM_FIELDS}
      mutation UpdateTeam($id: Int!, $input: UpdateTeamInput!) {
        updateTeam(id: $id, input: $input) {
          ...TeamFields
        }
      }
    `,
    { id, input }
  );
  return team;
};

export const deleteTeam = async (
  user: UserWithToken,
  id: number
): Promise<Team> => {
  const { deleteTeam: team } = await getGraphQLQueryClient(user).request<{
    deleteTeam: Team;
  }>(
    gql`
      ${TEAM_FIELDS}
      mutation DeleteTeam($id: Int!) {
        deleteTeam(id: $id) {
          ...TeamFields
        }
      }
    `,
    { id }
  );
  return team;
};

export const transferTeamOwnership = async (
  user: UserWithToken,
  teamId: number,
  newOwnerId: number
): Promise<Team> => {
  const { transferTeamOwnership: team } = await getGraphQLQueryClient(
    user
  ).request<{ transferTeamOwnership: Team }>(
    gql`
      ${TEAM_FIELDS}
      mutation TransferTeamOwnership($teamId: Int!, $newOwnerId: Int!) {
        transferTeamOwnership(teamId: $teamId, newOwnerId: $newOwnerId) {
          ...TeamFields
        }
      }
    `,
    { teamId, newOwnerId }
  );
  return team;
};

export interface InviteTeamMemberInput {
  teamId: number;
  userId: number;
  proposedPosition: TeamMemberPosition;
  message?: string;
}

export const inviteTeamMember = async (
  user: UserWithToken,
  input: InviteTeamMemberInput
): Promise<TeamInvitation> => {
  const { inviteTeamMember: invitation } = await getGraphQLQueryClient(
    user
  ).request<{ inviteTeamMember: TeamInvitation }>(
    gql`
      ${TEAM_INVITATION_FIELDS}
      mutation InviteTeamMember($input: InviteTeamMemberInput!) {
        inviteTeamMember(input: $input) {
          ...TeamInvitationFields
        }
      }
    `,
    { input }
  );
  return invitation;
};

export const cancelTeamInvitation = async (
  user: UserWithToken,
  invitationId: number
): Promise<TeamInvitation> => {
  const { cancelTeamInvitation: invitation } = await getGraphQLQueryClient(
    user
  ).request<{ cancelTeamInvitation: TeamInvitation }>(
    gql`
      ${TEAM_INVITATION_FIELDS}
      mutation CancelTeamInvitation($invitationId: Int!) {
        cancelTeamInvitation(invitationId: $invitationId) {
          ...TeamInvitationFields
        }
      }
    `,
    { invitationId }
  );
  return invitation;
};

export const respondToTeamInvitation = async (
  user: UserWithToken,
  invitationId: number,
  accept: boolean
): Promise<TeamInvitation> => {
  const { respondToTeamInvitation: invitation } = await getGraphQLQueryClient(
    user
  ).request<{ respondToTeamInvitation: TeamInvitation }>(
    gql`
      ${TEAM_INVITATION_FIELDS}
      mutation RespondToTeamInvitation($invitationId: Int!, $accept: Boolean!) {
        respondToTeamInvitation(invitationId: $invitationId, accept: $accept) {
          ...TeamInvitationFields
        }
      }
    `,
    { invitationId, accept }
  );
  return invitation;
};

export const updateTeamMember = async (
  user: UserWithToken,
  memberId: number,
  position: TeamMemberPosition
): Promise<TeamMember> => {
  const { updateTeamMember: member } = await getGraphQLQueryClient(user).request<{
    updateTeamMember: TeamMember;
  }>(
    gql`
      mutation UpdateTeamMember($memberId: Int!, $input: UpdateTeamMemberInput!) {
        updateTeamMember(memberId: $memberId, input: $input) {
          id
          teamId
          position
          joinedAt
          user {
            id
            name
            lastName
            picture
          }
        }
      }
    `,
    { memberId, input: { position } }
  );
  return member;
};

export const removeTeamMember = async (
  user: UserWithToken,
  memberId: number
): Promise<TeamMember> => {
  const { removeTeamMember: member } = await getGraphQLQueryClient(user).request<{
    removeTeamMember: TeamMember;
  }>(
    gql`
      mutation RemoveTeamMember($memberId: Int!) {
        removeTeamMember(memberId: $memberId) {
          id
          teamId
        }
      }
    `,
    { memberId }
  );
  return member;
};

export const leaveTeam = async (
  user: UserWithToken,
  teamId: number
): Promise<Team> => {
  const { leaveTeam: team } = await getGraphQLQueryClient(user).request<{
    leaveTeam: Team;
  }>(
    gql`
      ${TEAM_FIELDS}
      mutation LeaveTeam($teamId: Int!) {
        leaveTeam(teamId: $teamId) {
          ...TeamFields
        }
      }
    `,
    { teamId }
  );
  return team;
};
